import { Contract } from "@/modules/property-tracks/dtos/tenants";
import { Avatar, Typography, Grid, Stack } from "@mui/material";
import { PhoneIcon, MailIcon } from "lucide-react";
import { ContractStatusLabel } from "../contracts/tenant-contract";

const EntryLabel = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack gap={(theme) => theme.nawy.spacing.xxs}>
      <Typography
        fontSize={11}
        fontWeight={400}
        sx={{ color: (theme) => theme.nawy.colors.primitives.grey[700] }}
      >
        {label}
      </Typography>
      <Typography fontSize={14} fontWeight={600}>
        {children}
      </Typography>
    </Stack>
  );
};

const EntryContactInfo = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      height={36}
      borderRadius="10px"
      border="1px solid transparent"
      bgcolor={(theme) => theme.nawy.colors.primitives.white[200]}
      p={(theme) => theme.nawy.spacing.xs}
    >
      {children}
    </Stack>
  );
};

export const TenantContractEntry = ({
  tenantContract,
}: {
  tenantContract: Contract;
}) => {
  const formatDateRange = (startDate: string, endDate: string) => {
    const formatDate = (date: string) =>
      new Date(date).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });

    const endDateFormatted =
      new Date(endDate) > new Date() ? "Present" : formatDate(endDate);

    return `${formatDate(startDate)} - ${endDateFormatted}`;
  };
  return (
    <Stack
      width="100%"
      borderRadius="10px"
      border="1px solid #E0E0E0"
      py={(theme) => theme.nawy.spacing.sm}
      px={(theme) => theme.nawy.spacing.md}
      gap={(theme) => theme.nawy.spacing.sm}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={(theme) => theme.nawy.spacing.xxs}
      >
        <Avatar>{tenantContract.tenantContract.customerName.charAt(0)}</Avatar>
        <Stack gap={(theme) => theme.nawy.spacing.xxxs}>
          <Typography
            fontSize={11}
            fontWeight={400}
            sx={{ color: (theme) => theme.nawy.colors.primitives.grey[700] }}
          >
            {formatDateRange(tenantContract.startDate, tenantContract.endDate)}
          </Typography>
          <Typography fontSize={14} fontWeight={400}>
            {tenantContract.tenantContract.customerName}
          </Typography>
        </Stack>
      </Stack>

      <Stack gap={(theme) => theme.nawy.spacing.xxxs}>
        <EntryContactInfo>
          <PhoneIcon size={16} />
          <Typography fontSize={14} fontWeight={400}>
            {tenantContract.tenantContract.customerPhoneNumber}
          </Typography>
        </EntryContactInfo>

        <EntryContactInfo>
          <MailIcon size={16} />
          <Typography fontSize={14} fontWeight={400}>
            {tenantContract.tenantContract.customerEmail}
          </Typography>
        </EntryContactInfo>
      </Stack>

      <Grid container alignItems="center">
        <Grid size={{ mobile: 6 }}>
          <EntryLabel label="Contract Status">
            <ContractStatusLabel status={tenantContract.status} />
          </EntryLabel>
        </Grid>

        <Grid size={{ mobile: 6 }}>
          {tenantContract.tenantContract.rentStartValue ? (
            <EntryLabel label="Rental Amount">
              <Typography fontSize={14} fontWeight={400}>
                EGP{" "}
                {Number(
                  tenantContract.tenantContract.rentStartValue
                ).toLocaleString()}
              </Typography>
            </EntryLabel>
          ) : (
            <EntryLabel label="Rental Amount">
              <Typography fontSize={14} fontWeight={400}>
                ---
              </Typography>
            </EntryLabel>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
